import Alert from "components/common/Alert";
import { useEffect, useState, useRef, useMemo } from "react";
import { useOrganizers } from "components/company/hooks/useOrganizers";
import { usePavilions } from "components/pavilions/hooks/usePavilions";
import { useEventCategory } from "components/eventCategory/hooks/useEventCategory";
import { getExhibitorDropdown } from "api/exhibitor.api";
import { getImageUrl } from "utils/imageHelper";
import PageLayout from ".././PageFromLayout";
import {
  InputField,
  SelectField,
  ImageUploadField,
  TextareaField,
} from ".././FormFields";
import FormSection from ".././FormSection";
import { validateImageFile } from "utils/common";
export default function EventsForm({
  initialData = null,
  onSubmit,
  submitLabel,
  onBack,
}) {
  const { companies } = useOrganizers();
  const { loadPavilions, data: pavilions } = usePavilions();
  const { categories, loadCategoriesByOrganizer } = useEventCategory();
  const [errors, setErrors] = useState({});
  const [exhibitors, setExhibitors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState("");
  const alertRef = useRef(null);

  const [formData, setFormData] = useState({
    company_id: "",
    event_category_id: "",
    event_name: "",
    event_dates: [{ from_date: "", to_date: "" }],
    description: "",
    location: "",
    eventImage: [],
    eventVideo: [],
    default_media: "",
    exhibitors: [],
    status: "active",
  });

  const normalizePath = (path) => {
    if (!path || typeof path !== "string") return "";
    let cleaned = path
      .replace(/\\/g, "/")
      .replace(/\/{2,}/g, "/")
      .trim();
    cleaned = cleaned.replace(":/", "://");
    return cleaned;
  };

  const formatInputDateTimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const defaultMeta = useMemo(() => {
    if (!formData.default_media) return null;
    if (typeof formData.default_media === "object")
      return formData.default_media;
    try {
      return JSON.parse(formData.default_media);
    } catch {
      return null;
    }
  }, [formData.default_media]);

  const isDefaultMedia = (item, index, mediaType) => {
    if (!defaultMeta) return false;
    const isExisting = typeof item === "string";

    if (defaultMeta.type === "image" || defaultMeta.type === "video") {
      return (
        defaultMeta.type === mediaType &&
        isExisting &&
        normalizePath(defaultMeta.value) === normalizePath(item)
      );
    }

    if (defaultMeta.mediaType === mediaType) {
      if (defaultMeta.type === "existing" && isExisting) {
        return normalizePath(defaultMeta.value) === normalizePath(item);
      }
      if (defaultMeta.type === "new" && !isExisting) {
        return defaultMeta.value === index;
      }
    }

    return false;
  };

  const getAvailableExhibitors = (currentIndex) => {
    const selectedIds = formData.exhibitors
      .map((e, i) => (i !== currentIndex ? e.exhibitor_id : null))
      .filter(Boolean);
    return exhibitors.filter((e) => !selectedIds.includes(e._id));
  };

  useEffect(() => {
    if (!initialData?.event) return;

    setFormData({
      company_id: initialData.event.company_id?._id || "",
      event_category_id: initialData.event.event_category_id?._id || "",
      event_name: initialData.event.event_name || "",
      event_dates: (initialData.event.event_dates || []).map((d) => ({
        from_date: formatInputDateTimeLocal(d.from_date),
        to_date: formatInputDateTimeLocal(d.to_date),
      })),
      description: initialData.event.description || "",
      location: initialData.event.location || "",
      eventImage: initialData.event.eventImage || [],
      eventVideo: initialData.event.eventVideo || [],
      default_media: initialData.event.default_media || "",
      status: initialData.event.status || "active",
      exhibitors: (initialData.exhibitors || []).map((e) => ({
        exhibitor_id: e.exhibitor_id?._id,
        pavilion_id: e.pavilion_id?._id,
        stall_no: e.stall_no || "",
        floor_no: e.floor_no || "",
      })),
    });
  }, [initialData]);

  useEffect(() => {
    const companyId = localStorage.getItem("company_id");
    getExhibitorDropdown(companyId)
      .then((res) => setExhibitors(res.data || []))
      .catch(() => setExhibitors([]));
    loadPavilions(companyId);
    loadCategoriesByOrganizer(companyId);
  }, []);

  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [alert]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((p) => ({
      ...p,
      [name]: "",
    }));
  };

  const handleExhibitorChange = (index, key, value) => {
    const copy = [...formData.exhibitors];
    copy[index][key] = value;
    setFormData((p) => ({ ...p, exhibitors: copy }));
  };

  const handleMultimedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const images = [];
    const videos = [];

    // 1. Categorize files into images and videos
    files.forEach((file) => {
      if (file.type.startsWith("image/")) images.push(file);
      else if (file.type.startsWith("video/")) videos.push(file);
    });

    // 2. Validate ONLY images (e.g., max 5 MB per image)
    const imageValidationResults = await Promise.all(
      images.map((file) => validateImageFile(file,5 ))
    );

    // 3. Find if any image failed validation
    const invalidImage = imageValidationResults.find((res) => !res.isValid);

    if (invalidImage) {
      setErrors((prev) => ({
        ...prev,
        eventMedia: invalidImage.error,
      }));
      return; // Block update if any image is invalid
    }

    // 4. Clear previous error state
    setErrors((prev) => ({
      ...prev,
      eventMedia: "",
    }));

    // 5. Append validated images and raw videos to form state
    setFormData((prev) => ({
      ...prev,
      eventImage: [...(prev.eventImage || []), ...images],
      eventVideo: [...(prev.eventVideo || []), ...videos],
    }));
  };

  const addDateSlot = () => {
    setFormData((p) => ({
      ...p,
      event_dates: [...p.event_dates, { from_date: "", to_date: "" }],
    }));
  };

  const removeDateSlot = (index) => {
    setFormData((p) => ({
      ...p,
      event_dates: p.event_dates.filter((_, i) => i !== index),
    }));
  };

  const handleDateChange = (index, key, value) => {
    const updated = [...formData.event_dates];
    updated[index][key] = value;
    setFormData((p) => ({ ...p, event_dates: updated }));
  };

  const removeImage = (index) => {
    setFormData((p) => {
      const updated = [...p.eventImage];
      const removed = updated.splice(index, 1)[0];

      let newDefault = p.default_media;

      try {
        const parsed =
          typeof p.default_media === "object"
            ? p.default_media
            : JSON.parse(p.default_media || "{}");

        const removedPath = normalizePath(removed);
        const isBackendDefault =
          parsed.type === "image" &&
          normalizePath(parsed.value) === removedPath;

        const isFrontendDefault =
          parsed.mediaType === "image" &&
          ((parsed.type === "existing" &&
            normalizePath(parsed.value) === removedPath) ||
            (parsed.type === "new" && parsed.value === index));

        if (isBackendDefault || isFrontendDefault) newDefault = "";
      } catch {}

      return { ...p, eventImage: updated, default_media: newDefault };
    });
  };

  const removeVideo = (index) => {
    setFormData((p) => {
      const updated = [...p.eventVideo];
      updated.splice(index, 1);
      return { ...p, eventVideo: updated };
    });
  };

  const setDefaultMedia = (config) => {
    setFormData((prev) => ({
      ...prev,
      default_media: JSON.stringify({
        type: config.type,
        mediaType: config.mediaType,
        value: config.value,
      }),
    }));
  };

  const addExhibitor = () => {
    setFormData((p) => ({
      ...p,
      exhibitors: [
        ...p.exhibitors,
        { exhibitor_id: "", pavilion_id: "", stall_no: "", floor_no: "" },
      ],
    }));
  };

  const removeExhibitor = (index) => {
    setFormData((p) => ({
      ...p,
      exhibitors: p.exhibitors.filter((_, i) => i !== index),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!formData.event_category_id)
      newErrors.event_category_id = "Event category is required";
    if (!formData.event_name?.trim())
      newErrors.event_name = "Event name is required";
    if (!formData.location) newErrors.location = "Location is required";
    if (!formData.description)
      newErrors.description = "Description is required";

    if (!formData.event_dates || formData.event_dates.length === 0) {
      newErrors.date = "At least one event date is required";
    } else {
      let hasValidSlot = false;
      formData.event_dates.forEach((slot) => {
        if (slot.from_date && slot.to_date) {
          hasValidSlot = true;
          if (new Date(slot.from_date) > new Date(slot.to_date)) {
            newErrors.date = "Start date cannot be greater than End date";
          }
        }
      });
      if (!hasValidSlot)
        newErrors.date = "At least one start and end date must be filled";
    }

    // Exhibitor & Pavilion Mandatory Validation
    if (formData.exhibitors.length === 0) {
      newErrors.exhibitors = "Please add at least one exhibitor";
    } else {
      formData.exhibitors.forEach((ex, idx) => {
        if (!ex.exhibitor_id) {
          newErrors[`exhibitor_${idx}`] = "Exhibitor is required";
        }
        if (!ex.pavilion_id) {
          newErrors[`pavilion_${idx}`] = "Pavilion is required";
        }
      });
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setAlert("Please fix the errors below");
      return;
    }

    setErrors({});

    const fd = new FormData();

    const existingImages = formData.eventImage.filter(
      (img) => typeof img === "string"
    );
    const existingVideos = formData.eventVideo.filter(
      (vid) => typeof vid === "string"
    );

    fd.append("existing_eventImage", JSON.stringify(existingImages));
    fd.append("existing_eventVideo", JSON.stringify(existingVideos));

    formData.eventImage.forEach((img) => {
      if (img instanceof File) fd.append("eventImage", img);
    });
    formData.eventVideo.forEach((vid) => {
      if (vid instanceof File) fd.append("eventVideo", vid);
    });

    if (formData.default_media) {
      fd.append(
        "default_media",
        typeof formData.default_media === "object"
          ? JSON.stringify(formData.default_media)
          : formData.default_media
      );
    }

    fd.append("event_name", formData.event_name);
    fd.append("location", formData.location);
    fd.append("description", formData.description);
    fd.append("status", formData.status);
    fd.append("event_category_id", formData.event_category_id);

    const formattedDates = formData.event_dates
      .filter((d) => d.from_date && d.to_date)
      .map((d) => ({
        from_date: new Date(d.from_date).toISOString(),
        to_date: new Date(d.to_date).toISOString(),
      }));
    fd.append("event_dates", JSON.stringify(formattedDates));

    const cleanedExhibitors = formData.exhibitors
      .filter((ex) => ex.exhibitor_id && ex.pavilion_id)
      .map((ex) => ({
        exhibitor_id: ex.exhibitor_id,
        pavilion_id: ex.pavilion_id,
        stall_no: ex.stall_no,
        floor_no: ex.floor_no,
      }));
    // console.log("exhibitors", formData.exhibitors, cleanedExhibitors);
    fd.append("exhibitors", JSON.stringify(cleanedExhibitors));

    setSubmitting(true);
    try {
      await onSubmit(fd);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout
      onBack={onBack}
      onSubmit={submit}
      submitText={initialData ? "Update Event" : "Create Event"}
      loading={submitting}
    >
      <form onSubmit={submit} className="flex flex-col gap-6">
        {alert && (
          <div ref={alertRef} className="scroll-mt-[100px]">
            <Alert type="error" message={alert} onClose={() => setAlert("")} />
          </div>
        )}
        <FormSection title="Article Details">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <SelectField
              label="Event Category"
              name="event_category_id"
              value={formData.event_category_id || ""}
              onChange={handleChange}
              options={(categories || []).map((item) => ({
                value: item._id,
                label: item.title,
              }))}
              placeholder="Search category..."
              isSearchable={true}
              required
              error={errors.event_category_id}
            />
            <InputField
              label="Event Name"
              name="event_name"
              value={formData.event_name || ""}
              onChange={handleChange}
              placeholder="Enter name"
              error={errors.event_name}
              required
            />
            <InputField
              label="Location"
              name="location"
              value={formData.location || ""}
              onChange={handleChange}
              placeholder="Enter location"
              error={errors.location}
              required
            />
            <SelectField
              label="Event Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={[
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
              placeholder="Search status..."
              error={errors.status}
            />
            <TextareaField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter description ..."
              rows={3}
              error={errors.description}
            />
          </div>
        </FormSection>
        <FormSection title="Event Schedule & Dates">
          {formData.event_dates.map((slot, index) => (
            <div key={index} className="flex flex-col gap-1">
              {/* <div className="absolute -top-3 left-4 rounded-full bg-brand-500 px-3 py-1 text-xs text-white shadow">
                    Day {index + 1}
                  </div> */}
              <div className="flex flex-wrap items-center gap-3 ">
                <div className="min-w-[160px] flex-1">
                  <InputField
                    label="Start Date"
                    name="from_date"
                    type="datetime-local"
                    value={slot.from_date || ""}
                    onChange={(e) =>
                      handleDateChange(index, "from_date", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="min-w-[160px] flex-1">
                  <InputField
                    label="End Date"
                    name="to_date"
                    type="datetime-local"
                    value={slot.to_date || ""}
                    onChange={(e) =>
                      handleDateChange(index, "to_date", e.target.value)
                    }
                    required
                  />
                </div>

                <button
                  type="button"
                  onClick={() => removeDateSlot(index)}
                  className="mb-1 shrink-0 self-end p-2 font-bold text-red-500 hover:text-red-700"
                  title="Remove Tier"
                >
                  ✕
                </button>
              </div>
              {errors.date && (
                <p className="mt-2 text-sm text-red-500">{errors.date}</p>
              )}
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={addDateSlot}
                  className="btn-primary text-sm"
                >
                  + Add Days
                </button>
              </div>
            </div>
          ))}
        </FormSection>

        {/* <Section title="Event Details"> */}
        {/* <SelectInput
            label="Event Category"
            value={formData.event_category_id}
            required
            error={errors.event_category_id}
            onChange={(e) =>
              setFormData((p) => ({ ...p, event_category_id: e.target.value }))
            }
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.title}
              </option>
            ))}
          </SelectInput> */}

        {/* <Input
            label="Event Name"
            name="event_name"
            value={formData.event_name}
            onChange={handleChange}
            required
            error={errors.event_name}
          /> */}

        {/* <Input
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            error={errors.location}
          /> */}

        {/* <div className="mt-6 md:col-span-2">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">
                Event Dates
              </h3>
              <button
                type="button"
                onClick={addDateSlot}
                className="btn-primary"
              >
                + Add Days
              </button>
            </div>

            <div className="relative rounded-2xl border bg-white p-5 shadow-sm"></div>
            {errors.date && (
              <p className="mt-2 text-sm text-red-500">{errors.date}</p>
            )}
          </div> */}

        {/* <SelectInput
            label="Event Status"
            value={formData.status}
            onChange={(e) =>
              setFormData((p) => ({ ...p, status: e.target.value }))
            }
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </SelectInput> */}

        {/* <Textarea
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            error={errors.description}
          /> */}
        {/* </Section> */}

        <FormSection title="Event Media">
          <div className="md:col-span-2">
            {/* <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleMultimedia}
              className="w-full rounded-xl border p-3"
            /> */}
            <ImageUploadField
              // label="Gallery Images"
              multiple={true}
              // preview={formData.productImages||[]} // Array of URLs: ["blob:http...", "blob:http..."]
              onUpload={handleMultimedia}
              // onRemove={(index) => handleRemoveGalleryImage(index)}
              showRemove={true}
              // name="existingImages"
              accept="image/*,video/*"
              error={errors.eventMedia}
            />

            <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
              {formData.eventImage.map((img, i) => {
                const isExisting = typeof img === "string";
                const src = isExisting
                  ? getImageUrl(img)
                  : URL.createObjectURL(img);
                const isDefault = isDefaultMedia(img, i, "image");

                return (
                  <div
                    key={`img-${i}`}
                    className={`relative rounded-xl border p-2 transition ${
                      isDefault
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="h-32 w-full rounded-lg object-cover"
                    />
                    <div className="mt-2 flex justify-between">
                      {isDefault ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Image
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setDefaultMedia({
                              type: isExisting ? "existing" : "new",
                              mediaType: "image",
                              value: isExisting ? img : i,
                            })
                          }
                          className="text-xs text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="text-xs text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}

              {formData.eventVideo.map((vid, i) => {
                const isExisting = typeof vid === "string";
                const src = isExisting
                  ? getImageUrl(vid)
                  : URL.createObjectURL(vid);
                const isDefault = isDefaultMedia(vid, i, "video");

                return (
                  <div
                    key={`vid-${i}`}
                    className={`relative rounded-xl border p-2 transition ${
                      isDefault
                        ? "scale-[1.02] shadow-lg ring-2 ring-brand-500"
                        : "hover:shadow-md"
                    }`}
                  >
                    <video
                      src={src}
                      controls
                      className="h-32 w-full rounded-lg object-cover"
                    />
                    <div className="mt-2 flex justify-between">
                      {isDefault ? (
                        <span className="text-xs font-medium text-green-600">
                          Default Video
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setDefaultMedia({
                              type: isExisting ? "existing" : "new",
                              mediaType: "video",
                              value: isExisting ? vid : i,
                            })
                          }
                          className="text-xs text-blue-600"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeVideo(i)}
                        className="text-xs text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </FormSection>

        <FormSection title="Exhibitors">
          <div className="col-span-2 space-y-3">
            {formData.exhibitors.map((ex, i) => (
              <div key={i} className="flex items-end gap-2">
                <SelectField
                  value={ex.exhibitor_id}
                  onChange={(e) =>
                    handleExhibitorChange(i, "exhibitor_id", e.target.value)
                  }
                  options={getAvailableExhibitors(i).map((item) => ({
                    value: item._id,
                    label: item.title,
                  }))}
                  placeholder="Search Exhibitor..."
                  error={errors[`exhibitor_${i}`]}
                />
                {/* <select
                  className="w-full rounded-xl border p-3"
                  value={ex.exhibitor_id}
                  onChange={(e) =>
                    handleExhibitorChange(i, "exhibitor_id", e.target.value)
                  }
                >
                  <option value="">Exhibitor</option>
                  {getAvailableExhibitors(i).map((e) => (
                    <option key={e._id} value={e._id}>
                      {e.title}
                    </option>
                  ))}
                </select> */}
                <SelectField
                  value={ex.pavilion_id}
                  onChange={(e) =>
                    handleExhibitorChange(i, "pavilion_id", e.target.value)
                  }
                  options={(pavilions || []).map((item) => ({
                    value: String(item._id), // Force string conversion for consistency
                    label: item.pavilion_name,
                  }))}
                  placeholder="Search Pavilion..."
                  error={errors[`pavilion_${i}`]}
                />
                {/* <select
                  className="w-fullpavilions rounded-xl border p-3"
                  value={ex.pavilion_id}
                  onChange={(e) =>
                    handleExhibitorChange(i, "pavilion_id", e.target.value)
                  }
                >
                  <option value="">Pavilion</option>
                  {.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.pavilion_name}
                    </option>
                  ))}
                </select> */}

                {/* <input
                  className="w-28 rounded-xl border p-3"
                  placeholder="Stall"
                  value={ex.stall_no}
                  onChange={(e) =>
                    handleExhibitorChange(i, "stall_no", e.target.value)
                  }
                /> */}
                <InputField
                  value={ex.stall_no}
                  onChange={(e) =>
                    handleExhibitorChange(i, "stall_no", e.target.value)
                  }
                  placeholder="Stall"
                />
                <InputField
                  value={ex.floor_no}
                  onChange={(e) =>
                    handleExhibitorChange(i, "floor_no", e.target.value)
                  }
                  placeholder="Floor No"
                />
                {/* <input
                  className="w-28 rounded-xl border p-3"
                  placeholder="Floor No."
                  value={ex.floor_no || ""}
                  onChange={(e) =>
                    handleExhibitorChange(i, "floor_no", e.target.value)
                  }
                /> */}

                <button
                  type="button"
                  onClick={() => removeExhibitor(i)}
                  className="mb-1 shrink-0 self-center p-2 font-bold text-red-500 hover:text-red-700"
                  title="Remove Tier"
                >
                  ✕
                </button>
              </div>
            ))}
            {errors.exhibitors && (
              <p className="mt-1 text-sm text-red-500">{errors.exhibitors}</p>
            )}
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={addExhibitor}
                className="btn-primary"
              >
                + Add Exhibitor
              </button>
            </div>
          </div>
        </FormSection>

        {/* <div className="flex justify-between">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl bg-red-500 px-6 py-3 text-white"
            >
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-xl bg-brand-500 px-6 py-3 text-white"
          >
            {submitLabel || (initialData ? "Update Event" : "Create Event")}
          </button>
        </div> */}
        {/* <div className="button-div mt-6 flex justify-between">
          {onBack && (
            <button type="button" onClick={onBack} className="btn-cancel">
              Back
            </button>
          )}
          <button type="submit" className="btn-primary flex items-center gap-2">
            <span>
              {submitLabel || (initialData ? "Update Event" : "Create Event")}
            </span>
            <FiArrowRight size={16} />
          </button>
        </div> */}
      </form>
    </PageLayout>
  );
}

// function Section({ title, children }) {
//   return (
//     <section>
//       <h3 className="mb-4 text-lg font-semibold">{title}</h3>
//       <div className="grid grid-cols-1 gap-5 md:grid-cols-2">{children}</div>
//     </section>
//   );
// }

// function Input({ label, required, error, ...props }) {
//   return (
//     <div>
//       <label className="mb-1 block text-sm font-medium">
//         {label} {required && <span className="text-red-500">*</span>}
//       </label>
//       <input
//         {...props}
//         className={`w-full rounded-xl border p-3 ${
//           error ? "border-red-500" : ""
//         }`}
//       />
//       {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
//     </div>
//   );
// }

// function Textarea({ label, required, error, ...props }) {
//   return (
//     <div className="md:col-span-2">
//       <label className="mb-1 block text-sm font-medium">
//         {label} {required && <span className="text-red-500">*</span>}
//       </label>
//       <textarea
//         {...props}
//         className={`w-full rounded-xl border p-3 ${
//           error ? "border-red-500" : ""
//         }`}
//       />
//       {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
//     </div>
//   );
// }

// function SelectInput({ label, required, error, children, ...props }) {
//   return (
//     <div>
//       <label className="mb-1 block text-sm font-medium">
//         {label} {required && <span className="text-red-500">*</span>}
//       </label>
//       <select
//         {...props}
//         className={`w-full rounded-xl border p-3 ${
//           error ? "border-red-500" : ""
//         }`}
//       >
//         {children}
//       </select>
//       {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
//     </div>
//   );
// }
